
import { useNavigate } from 'react-router-dom';
import { useOnboardingState } from '@/hooks/useOnboardingState';
import { useOnboardingTracking } from '@/hooks/useOnboardingTracking';
import { useEarlyAccessSubmission } from '@/hooks/useEarlyAccessSubmission';
import { usePageTracking } from '@/hooks/usePageTracking';
import { onboardingQuestions } from '@/data/onboardingData';
import OnboardingProgress from '@/components/onboarding/OnboardingProgress';
import OnboardingCard from '@/components/onboarding/OnboardingCard';
import OnboardingNavigation from '@/components/onboarding/OnboardingNavigation';
import OnboardingThankYou from '@/components/onboarding/OnboardingThankYou';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';

const PreOnboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isExistingUser, setIsExistingUser] = useState(false);
  
  const {
    currentStep,
    setCurrentStep,
    isComplete,
    setIsComplete,
    isTransitioning,
    transitionDirection,
    cardTransitionState,
    showConfetti,
    formData,
    handleInputChange,
    canProceed,
    executeStepTransition
  } = useOnboardingState();

  // Analytics tracking
  usePageTracking('Pre-Onboarding');
  const { trackStepComplete, trackOnboardingFinish } = useOnboardingTracking();
  
  // Supabase submission
  const { submitEarlyAccess, isSubmitting } = useEarlyAccessSubmission();

  const totalSteps = 6;
  const currentQuestion = onboardingQuestions[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const getStepName = (step: number) => {
    const stepNames = [
      'Name and Email',
      'Motivation',
      'Experience',
      'Challenge',
      'Use Case',
      'Interview Interest'
    ];
    return stepNames[step] || `Step ${step + 1}`;
  };

  const getUserResponse = (step: number) => {
    switch (step) {
      case 0: return `${formData.firstName} - ${formData.email}`;
      case 1: return formData.motivation;
      case 2: return formData.experience;
      case 3: return formData.challenge;
      case 4: return formData.useCase;
      case 5: return formData.interviewInterest;
      default: return '';
    }
  };

  const handleNext = async () => {
    if (isTransitioning || isSubmitting) return;
    
    // Track step completion
    trackStepComplete(currentStep, getStepName(currentStep), getUserResponse(currentStep));
    
    if (isLastStep) {
      executeStepTransition('forward', async () => {
        console.log('Completing onboarding with data:', formData);
        
        // Submit to Supabase
        const result = await submitEarlyAccess(formData);
        
        if (result.success) {
          console.log('Successfully saved to Supabase');
          
          // Check if this was an existing user
          setIsExistingUser(result.isExistingUser || false);
          
          // Track completion
          trackOnboardingFinish(formData);
          setIsComplete(true);
          localStorage.removeItem('show-up-onboarding');
        } else {
          console.error('Failed to save to Supabase:', result.error);
          toast({
            title: "Error saving data",
            description: "There was a problem saving your information. Please try again.",
            variant: "destructive"
          });
        }
      });
    } else {
      executeStepTransition('forward', () => {
        setCurrentStep(prev => prev + 1);
      });
    }
  };

  const handleBack = () => {
    if (isTransitioning || isSubmitting) return;
    
    if (isFirstStep) {
      navigate('/');
    } else {
      executeStepTransition('backward', () => {
        setCurrentStep(prev => prev - 1);
      });
    }
  };

  const handleFollowX = () => {
    window.open('https://twitter.com/showupagain', '_blank');
  };

  const handleClose = () => {
    navigate('/');
  };

  // Thank You Page
  if (isComplete) {
    return (
      <OnboardingThankYou 
        onFollowX={handleFollowX}
        onClose={handleClose}
        showConfetti={showConfetti}
        isExistingUser={isExistingUser}
      />
    );
  }

  // Main Onboarding Flow
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 via-purple-400 to-indigo-500 flex items-center justify-center px-4 overflow-hidden">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg animate-bounce-in">
            <img 
              src="/lovable-uploads/176a7476-cd67-4fa3-8321-efb0ad50558c.png" 
              alt="Show Up! Logo"
              className="w-10 h-10 object-contain"
            />
          </div>
          <h1 className="text-2xl font-black text-white mb-2">Show Up!</h1>
          <p className="text-white/80 font-medium">This will take 60 seconds</p>
        </div>

        {/* Progress Bar */}
        <OnboardingProgress currentStep={currentStep} totalSteps={totalSteps} />

        {/* Question Card */}
        <OnboardingCard
          currentStep={currentStep}
          question={currentQuestion}
          formData={formData}
          onInputChange={handleInputChange}
          cardTransitionState={cardTransitionState}
          transitionDirection={transitionDirection}
        />

        {/* Navigation Buttons */}
        <OnboardingNavigation
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          canProceed={canProceed()}
          isTransitioning={isTransitioning || isSubmitting}
          onBack={handleBack}
          onNext={handleNext}
        />

        {/* Footer */}
        <div className="text-center mt-8 animate-fade-in">
          <p className="text-white/60 text-sm">
            Your information helps us create a better experience for you
          </p>
        </div>
      </div>
    </div>
  );
};

export default PreOnboarding;
